import * as React from 'react';

const FORM_STATES = {
  Busy: 'Busy',
  Error: 'Error',
  Invalid: 'Invalid',
  Ready: 'Ready',
  Success: 'Success',
};

function getFormValues(form) {
  if (form instanceof HTMLFormElement) {
    const formData = new FormData(form);
    return Object.fromEntries(formData.entries());
  }
  return {};
}

export default function useForm({ onSubmit, onChange, onInvalid } = {}) {
  const [formState, setFormState] = React.useState(FORM_STATES.Ready);
  const [formValues, setFormValues] = React.useState({});

  async function handleSubmit(event) {
    event.preventDefault();
    if (formState !== FORM_STATES.Busy) {
      setFormState(FORM_STATES.Busy);
      setFormValues(getFormValues(event.currentTarget));
      try {
        if (onSubmit) {
          const submitter = event.submitter || event.nativeEvent.submitter;
          const submitterValue = (
            submitter?.value ||
            submitter?.textContent ||
            ''
          ).trim();
          await onSubmit(event, { formValues, submitterValue });
        }
        setFormState(FORM_STATES.Success);
      } catch (error) {
        // TODO Handle Error
        console.warn(error.message);
        setFormState(FORM_STATES.Error);
      }
    }
  }

  function handleInvalid(event) {
    setFormValues(getFormValues(event.currentTarget));
    onInvalid?.(event);
    setFormState(FORM_STATES.Invalid);
  }

  function handleChange(event) {
    setFormValues(getFormValues(event.currentTarget));
    onChange?.(event);
    if (
      formState === FORM_STATES.Invalid &&
      event.target.checkValidity() &&
      Array.from(event.target.form.elements).every(
        (el) => !!el?.validity?.valid
      )
    ) {
      setFormState(FORM_STATES.Ready);
    }
  }

  return {
    getFormProps: (props = {}) => ({
      'aria-busy': formState === FORM_STATES.Busy || undefined,
      'aria-live': 'polite',
      'data-invalid': formState === FORM_STATES.Invalid || undefined,
      ...props,
      onChange: handleChange,
      onInvalid: handleInvalid,
      onSubmit: handleSubmit,
    }),
    formState,
    formStates: FORM_STATES,
    formValues,
    isBusy: formState === FORM_STATES.Busy,
    isInvalid: formState === FORM_STATES.Invalid,
    isSubmitted:
      formState === FORM_STATES.Error || formState === FORM_STATES.Success,
    isSubmitFailure: formState === FORM_STATES.Error,
    isSubmitSuccess: formState === FORM_STATES.Success,
  };
}
